import React, { useState, useEffect, useCallback } from 'react';
import { Video, Download, Search, Settings2, Flag, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const WARDS = ['Ward 1', 'Ward 2', 'Ward 3', 'Ward 4', 'Ward 5', 'Ward 6', 'Ward 7', 'Ward 8'];
const STATUS_LABELS = {
  submitted: 'Submitted', under_review: 'Under Review', follow_up_needed: 'Follow-Up Needed',
  selected: 'Selected', not_selected: 'Not Selected', withdrawn: 'Withdrawn'
};
const STATUS_COLORS = {
  submitted: 'bg-slate-100 text-slate-700', under_review: 'bg-amber-100 text-amber-800',
  follow_up_needed: 'bg-orange-100 text-orange-800', selected: 'bg-emerald-100 text-emerald-800',
  not_selected: 'bg-rose-100 text-rose-700', withdrawn: 'bg-slate-200 text-slate-500'
};
const inputCls = "border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500";

export const StrongBeginningsTab = ({ API_URL, getAuthHeader }) => {
  const [data, setData] = useState({ total: 0, applications: [], ward_counts: {} });
  const [filters, setFilters] = useState({ search: '', ward: '', program_type: '', licensing: '', status: '', date_from: '', date_to: '' });
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState('');
  const [settings, setSettings] = useState(null);

  const fetchApps = useCallback(async (f) => {
    try {
      const qs = new URLSearchParams(f);
      const r = await fetch(`${API_URL}/api/strong-beginnings/applications?${qs}`, { headers: getAuthHeader() });
      if (r.ok) setData(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  const fetchSettings = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/strong-beginnings/admin-settings`, { headers: getAuthHeader() });
      if (r.ok) setSettings(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => {
    const t = setTimeout(() => fetchApps(filters), 300);
    return () => clearTimeout(t);
  }, [filters, fetchApps]);

  const saveSettings = async () => {
    try {
      const r = await fetch(`${API_URL}/api/strong-beginnings/admin-settings`, {
        method: 'PUT', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ deadline: settings.deadline, notify_email: settings.notify_email })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setSettings(d);
      toast.success(`Settings saved — applications are ${d.open ? 'OPEN' : 'CLOSED'}`);
    } catch (e) { toast.error(e.message); }
  };

  const setStatus = async (a, status) => {
    try {
      const r = await fetch(`${API_URL}/api/strong-beginnings/applications/${a.id}/status`, {
        method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Update failed');
      toast.success(`${a.reference} → ${STATUS_LABELS[status]}. No email was sent to the applicant.`);
      fetchApps(filters);
    } catch (e) { toast.error(e.message); }
  };

  const addNote = async (a) => {
    if (!note.trim()) return;
    try {
      const r = await fetch(`${API_URL}/api/strong-beginnings/applications/${a.id}/notes`, {
        method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
      if (!r.ok) throw new Error('Could not add note');
      setNote('');
      toast.success('Note added');
      fetchApps(filters);
    } catch (e) { toast.error(e.message); }
  };

  const exportCsv = async () => {
    try {
      const qs = new URLSearchParams(filters);
      const r = await fetch(`${API_URL}/api/strong-beginnings/applications.csv?${qs}`, { headers: getAuthHeader() });
      if (!r.ok) throw new Error('Export failed');
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = 'strong_beginnings_applications.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const Detail = ({ a }) => (
    <div className="bg-slate-50 rounded-xl p-5 space-y-4 text-sm" data-testid={`sb-admin-detail-${a.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
        {[['Contact', `${a.first_name} ${a.last_name} (${a.role_title})`], ['Email', a.email], ['Phone', a.phone],
          ['Preferred contact', `${a.preferred_contact}${a.preferred_language ? ` · ${a.preferred_language}` : ''}`],
          ['Address', `${a.address1}${a.address2 ? ', ' + a.address2 : ''}, ${a.city}, ${a.state} ${a.zip_code}`],
          ['Licensing', `${a.licensing_status}${a.licensing_other ? ` — ${a.licensing_other}` : ''}`],
          ['Website', a.website || '—'], ['Years in operation', a.years_in_operation || '—'],
          ['Age groups', (a.age_groups || []).join(', ')], ['Languages used', a.languages_used || '—'],
          ['Participation', a.participation_willing === 'yes' ? 'Yes' : 'Would like to discuss'],
          ['Scheduling', a.scheduling_preferences || '—'], ['Access needs', a.access_needs || '—'],
          ['How heard', a.how_heard || '—']].map(([l, v]) => (
          <p key={l}><span className="font-semibold text-slate-600">{l}:</span> <span className="text-slate-700">{v}</span></p>
        ))}
      </div>
      {[['What makes the program special', a.story_special], ['For families & the DC community', a.story_families], ['Highlights', a.story_highlight]].map(([l, v]) => v && (
        <div key={l}>
          <p className="font-semibold text-slate-600 mb-1">{l}</p>
          <p className="text-slate-700 whitespace-pre-wrap bg-white border border-slate-200 rounded-lg p-3">{v}</p>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-4 pt-1">
        {a.licensing_flagged && <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700"><Flag className="w-3.5 h-3.5" /> Licensing flagged for DCELC review</span>}
        <span className={`text-xs font-bold ${a.keep_on_file ? 'text-teal-700' : 'text-slate-400'}`} data-testid={`sb-keep-on-file-${a.id}`}>
          {a.keep_on_file ? '✓ Opted into future opportunities' : 'Did not opt into future opportunities'}
        </span>
        <span className="text-xs text-slate-400">Confirmation email: {(a.confirmation_email || {}).status || 'pending'}</span>
      </div>
      <div className="border-t border-slate-200 pt-3">
        <p className="font-semibold text-slate-600 mb-2">Status history</p>
        <ul className="text-xs text-slate-500 space-y-1">
          {(a.status_history || []).map((h, i) => (
            <li key={i}>{(h.at || '').slice(0, 16).replace('T', ' ')} — {h.from ? `${STATUS_LABELS[h.from]} → ` : ''}{STATUS_LABELS[h.to] || h.to} ({h.by})</li>
          ))}
        </ul>
      </div>
      <div className="border-t border-slate-200 pt-3">
        <p className="font-semibold text-slate-600 mb-2">Internal notes</p>
        {(a.notes || []).length > 0 && (
          <ul className="space-y-2 mb-3">
            {a.notes.map((n, i) => (
              <li key={i} className="bg-white border border-slate-200 rounded-lg p-2.5 text-slate-700">
                {n.text}
                <span className="block text-[11px] text-slate-400 mt-1">{n.by} · {(n.at || '').slice(0, 16).replace('T', ' ')}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add an internal note…" aria-label="Internal note"
            className={`${inputCls} flex-1`} data-testid={`sb-note-input-${a.id}`} />
          <button onClick={() => addNote(a)} className="px-4 py-2 text-xs font-bold text-white bg-slate-700 hover:bg-slate-800 rounded-lg" data-testid={`sb-add-note-${a.id}`}>Add Note</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="strong-beginnings-admin">
      <div className="bg-gradient-to-r from-[#0a1628] to-teal-900 rounded-xl px-6 py-5 text-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-lime-300 mb-1 flex items-center gap-2"><Video className="w-3.5 h-3.5" /> Strong Beginnings</p>
          <h3 className="text-lg font-bold">Video Series Applications</h3>
          <p className="text-white/70 text-xs mt-0.5">{data.total} application{data.total !== 1 ? 's' : ''} shown · goal: 1 center-based + 1 home-based per ward</p>
        </div>
        <button onClick={exportCsv} data-testid="sb-export-csv" className="inline-flex items-center gap-2 px-4 py-2 border border-white/30 hover:bg-white/10 rounded-lg text-sm font-semibold">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Deadline settings */}
      {settings && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-end gap-4 flex-wrap" data-testid="sb-settings-panel">
          <div className="flex items-center gap-2 pb-2 text-slate-700"><Settings2 className="w-4 h-4" /><span className="text-sm font-bold">Application settings</span></div>
          <div>
            <label htmlFor="sb-deadline" className="block text-xs font-medium text-slate-600 mb-1">Deadline (open through this date, ET)</label>
            <input id="sb-deadline" type="date" value={settings.deadline} onChange={e => setSettings(s => ({ ...s, deadline: e.target.value }))} className={inputCls} data-testid="sb-deadline-input" />
          </div>
          <div>
            <label htmlFor="sb-notify" className="block text-xs font-medium text-slate-600 mb-1">Internal notification email</label>
            <input id="sb-notify" value={settings.notify_email} onChange={e => setSettings(s => ({ ...s, notify_email: e.target.value }))} className={`${inputCls} w-64`} data-testid="sb-notify-input" />
          </div>
          <button onClick={saveSettings} className="px-4 py-2 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg" data-testid="sb-save-settings">Save Settings</button>
          <span className={`pb-2 text-xs font-bold ${settings.open ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="sb-open-indicator">
            Applications are currently {settings.open ? 'OPEN' : 'CLOSED'}
          </span>
        </div>
      )}

      {/* Ward counts */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 overflow-x-auto" data-testid="sb-ward-counts">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-500 uppercase">
              <th className="text-left py-1.5 px-2">Applications</th>
              {WARDS.map(w => <th key={w} className="text-center py-1.5 px-2">{w.replace('Ward ', 'W')}</th>)}
            </tr>
          </thead>
          <tbody>
            {[['Center-based', 'center'], ['Home-based', 'home']].map(([label, k]) => (
              <tr key={k} className="border-t border-slate-100">
                <td className="py-1.5 px-2 font-semibold text-slate-700">{label}</td>
                {WARDS.map(w => {
                  const c = (data.ward_counts[w] || {});
                  return (
                    <td key={w} className={`text-center py-1.5 px-2 font-bold ${c[k] ? 'text-teal-700' : 'text-slate-300'}`}>
                      {c[k] || 0}{c[`selected_${k}`] ? <span className="text-emerald-600"> ✓</span> : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[11px] text-slate-400 mt-2">✓ = a program in that ward/type has been marked Selected</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-end gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            placeholder="Program, contact or email…" aria-label="Search applications" className={`${inputCls} pl-9 w-64`} data-testid="sb-search-input" />
        </div>
        <select value={filters.ward} onChange={e => setFilters(f => ({ ...f, ward: e.target.value }))} aria-label="Filter by ward" className={inputCls} data-testid="sb-filter-ward">
          <option value="">All wards</option>{WARDS.map(w => <option key={w}>{w}</option>)}
        </select>
        <select value={filters.program_type} onChange={e => setFilters(f => ({ ...f, program_type: e.target.value }))} aria-label="Filter by program type" className={inputCls} data-testid="sb-filter-type">
          <option value="">All types</option><option>Center-based child care</option><option>Home-based child care</option>
        </select>
        <select value={filters.licensing} onChange={e => setFilters(f => ({ ...f, licensing: e.target.value }))} aria-label="Filter by licensing" className={inputCls} data-testid="sb-filter-licensing">
          <option value="">All licensing</option><option>Currently licensed</option><option>Licensing in progress</option><option>Other</option>
        </select>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} aria-label="Filter by status" className={inputCls} data-testid="sb-filter-status">
          <option value="">All statuses</option>{Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div>
          <label htmlFor="sb-from" className="block text-[11px] text-slate-500">From</label>
          <input id="sb-from" type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className={inputCls} data-testid="sb-filter-from" />
        </div>
        <div>
          <label htmlFor="sb-to" className="block text-[11px] text-slate-500">To</label>
          <input id="sb-to" type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className={inputCls} data-testid="sb-filter-to" />
        </div>
      </div>

      {/* List */}
      {data.applications.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500" data-testid="sb-empty">
          <Video className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="font-semibold text-slate-700 mb-1">No applications found</p>
          <p className="text-sm">New applications from /strong-beginnings will appear here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.applications.map(a => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`sb-app-row-${a.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3">
                <button onClick={() => setOpen(open === a.id ? null : a.id)} className="flex-1 min-w-[220px] text-left" data-testid={`sb-app-toggle-${a.id}`}>
                  <p className="font-bold text-slate-800">{a.program_name} {a.licensing_flagged && <Flag className="w-3.5 h-3.5 text-amber-500 inline" aria-label="Licensing flagged" />}</p>
                  <p className="text-xs text-slate-500">{a.reference} · {a.program_type} · {a.ward} · {a.first_name} {a.last_name} · {(a.submitted_at || '').slice(0, 10)}</p>
                </button>
                <div className="flex items-center gap-2">
                  {a.keep_on_file && <span className="text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5">Future opps ✓</span>}
                  <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${STATUS_COLORS[a.status]}`} data-testid={`sb-status-badge-${a.id}`}>{STATUS_LABELS[a.status]}</span>
                  <select value={a.status} onChange={e => setStatus(a, e.target.value)} aria-label={`Change status for ${a.program_name}`} className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid={`sb-status-select-${a.id}`}>
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <button onClick={() => setOpen(open === a.id ? null : a.id)} aria-label={open === a.id ? 'Collapse' : 'Expand'} className="text-slate-400 p-1">
                    {open === a.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {open === a.id && <div className="px-4 pb-4">{Detail({ a })}</div>}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">Status changes never email applicants automatically — selection messages must be sent manually by an administrator.</p>
    </div>
  );
};
